/** Which message channel this install is actually on.
 *
 *  A firm-wide setting, so it deserves a firm-wide answer. The People page used to
 *  read it off `rows[0].channel` — the per-user copy the `/users` payload carries —
 *  which meant an install with no people yet silently reported WhatsApp, and a
 *  page-level fact was being inferred from the first row of a list.
 *
 *  `GET /channel` is the install's own answer. It costs nothing extra: the Message
 *  channel screen already queries it under this exact key, so react-query serves both
 *  from one cache entry, and that screen already invalidates the key after a switch —
 *  so every consumer updates without knowing about any of the others.
 *
 *  Owner-only, like the endpoint. Every screen that needs it is owner-only too.
 */
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

export type ChannelOption = {
  value: string; label: string; configured: boolean; supports_receipts: boolean
  ready: number; of: number
  not_ready: { user_id: string; name: string; why: string }[]
}
export type ChannelStatus = { active: string; options: ChannelOption[] }

export function useChannel() {
  const q = useQuery({
    queryKey: ['channel'],
    queryFn: () => api.get<ChannelStatus>('/channel'),
  })
  const name = q.data?.active ?? ''
  return {
    name,
    onTelegram: name === 'telegram',
    // The server owns the display name, so there is no second table of channel
    // labels to keep in step with the first.
    label: q.data?.options.find((o) => o.value === name)?.label ?? '',
    // Callers must not render either channel's controls until this is false: one of
    // them is a consent tick, and guessing wrong then swapping is worse than a beat
    // of nothing.
    loading: q.isLoading,
    error: q.error,
  }
}
